import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import TrendingSidebar from '@/components/base/TrendingSidebar';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id, 10) : 0;
  const article = getArticleById(articleId);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
            <i className="ri-emotion-sad-line text-2xl text-foreground-500"></i>
          </span>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章不存在</h2>
          <p className="text-sm text-foreground-600 mb-4">该文章可能已被删除或链接有误</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.id);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '91吃瓜网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91chigua.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <div className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 mt-4">
            {/* Article Content */}
            <article className="flex-1 min-w-0">
              <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                {/* Cover Image */}
                <div className="aspect-[21/9] overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-6 md:p-8">
                  {/* Meta */}
                  <div className="flex items-center gap-3 flex-wrap mb-4">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="px-3 py-1 text-xs font-medium bg-primary-50 text-primary-500 rounded-full whitespace-nowrap hover:bg-primary-100 transition-colors"
                    >
                      {article.categoryName}
                    </Link>
                    <span className="text-xs text-foreground-500">{article.publishDate}</span>
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </span>
                      {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
                    </span>
                    <span className="text-xs text-foreground-500">作者：{article.author}</span>
                  </div>

                  {/* Title */}
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-6">
                    {article.title}
                  </h1>

                  {/* Tags */}
                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 text-xs bg-secondary-50 text-secondary-600 rounded-md whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Content */}
                  <div
                    className="article-content prose prose-sm max-w-none text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section className="mt-10">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-links-line"></i>
                    </span>
                    <h2 className="text-lg font-semibold text-foreground-900">相关吃瓜</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {relatedArticles.map((related) => (
                      <ArticleCard key={related.id} article={related} />
                    ))}
                  </div>
                </section>
              )}
            </article>

            {/* Sidebar */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <div className="lg:sticky lg:top-20">
                <TrendingSidebar />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .article-content p {
          margin-bottom: 1.25em;
          line-height: 1.8;
          font-size: 15px;
          color: oklch(var(--foreground-800));
        }
        .article-content p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </>
  );
}